import { IconButton, Tag } from '@ads/components-react';
import { DocumentTextIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentHeaderViewModel } from '@/shared/api/generated';
import { Skeleton } from '@/shared/ui/atoms';

import { usePaymentTypeLabel } from '../../../hooks/usePaymentTypeLabel';
import type { ParcelSummary } from '../../../types/parcels';
import { EMPTY_VALUE, formatCurrency } from '../../../utils/formatCurrency';
import { isCardPaymentType } from '../../../utils/parcelOperationAvailability';

export interface ContractHeaderCardProps {
  readonly header: IndividualPaymentHeaderViewModel | undefined;
  readonly isLoading: boolean;
  /**
   * Resumo derivado das parcelas, que alimenta as linhas de apoio dos
   * indicadores. `null` enquanto as parcelas não carregaram — as linhas de apoio
   * são omitidas nesse caso, em vez de exibirem zeros.
   */
  readonly summary: ParcelSummary | null;
}

/** Um segmento da régua de indicadores. */
function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string | null | undefined;
}) {
  return (
    <div className="flex min-w-none flex-1 flex-col gap-quark px-mini">
      <span className="ts-subtitle-xxs text-ctx-content-placeholder">{label}</span>
      <span className="ts-heading-md tabular-nums text-ctx-content-title">{value}</span>
      {/* Altura reservada mesmo sem hint, para os quatro valores ficarem na
          mesma linha de base quando só alguns têm linha de apoio. */}
      <span className="min-h-mini ts-caption-xxs text-ctx-content-placeholder">{hint ?? ''}</span>
    </div>
  );
}

/**
 * Último cartão do contrato — linha de apoio sob a forma de pagamento.
 *
 * Sem rótulo próprio: o cartão **é** a forma de pagamento, e um campo separado
 * repetiria o contexto que a linha de cima já deu. Fica na tipografia de hint,
 * como as outras linhas de apoio do card.
 *
 * Regra do legado (`#toggleLastCreditCard`): alterna entre o número e a máscara.
 * Aqui a máscara acompanha o tamanho do valor em vez de ser fixa em 16
 * caracteres — insinuar um número completo onde o backend mandou só o final seria
 * mentir sobre o dado.
 *
 * Começa oculto de propósito: é dado de pagamento numa tela que o operador deixa
 * aberta, e revelar exige um gesto.
 */
function LastCreditCardLine({ card }: { readonly card: string }) {
  const { t } = useTranslation('individualPayment');
  const [isVisible, setIsVisible] = useState(false);

  return (
    <span className="flex items-center gap-quark">
      <span className="break-words ts-caption-xxs tabular-nums text-ctx-content-placeholder">
        {isVisible ? card : '•'.repeat(card.length)}
      </span>
      <IconButton
        icon={isVisible ? EyeSlashIcon : EyeIcon}
        hierarchy="tertiary"
        size="sm"
        ariaLabel={isVisible ? t('parcels.contract.hideCard') : t('parcels.contract.showCard')}
        onClick={() => setIsVisible(current => !current)}
      />
    </span>
  );
}

/** Um campo chave-valor do card de dados. */
function Field({
  label,
  value,
  hint,
  children,
}: {
  label: string;
  value?: string | undefined;
  hint?: string | null | undefined;
  children?: ReactNode | undefined;
}) {
  return (
    <div className="flex min-w-none flex-col gap-quark">
      <span className="ts-subtitle-xxs text-ctx-content-placeholder">{label}</span>
      {children ?? (
        <span className="break-words ts-paragraph-xs text-ctx-content-title">
          {value ?? EMPTY_VALUE}
        </span>
      )}
      {hint && (
        <span className="break-words ts-caption-xxs text-ctx-content-placeholder">{hint}</span>
      )}
    </div>
  );
}

function StatRowSkeleton() {
  return (
    <div className="flex items-center rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-mini py-xxs">
      {Array.from({ length: 4 }, (_, index) => `stat-${index}`).map((key, index) => (
        <div key={key} className="flex items-center">
          {index > 0 && <div className="mx-none h-mini w-px bg-ctx-layout-border" />}
          <div className="flex flex-col gap-nano px-mini">
            <Skeleton className="h-mini w-[7.5rem] rounded-sm" />
            <Skeleton className="h-xs w-[9.5rem] rounded-sm" />
            <Skeleton className="h-mini w-[6rem] rounded-sm" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ContractCardSkeleton() {
  return (
    <section className="flex flex-col rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
      <header className="flex items-center gap-nano border-b-hairline border-ctx-layout-border px-xxs py-micro">
        <Skeleton className="h-mini w-mini rounded-sm" />
        <Skeleton className="h-xs w-[11rem] rounded-sm" />
      </header>
      <div className="flex flex-col gap-xxxs px-xxs py-xxxs">
        {Array.from({ length: 3 }, (_, row) => `row-${row}`).map(rowKey => (
          <div key={rowKey} className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
            {Array.from({ length: 3 }, (_, col) => `${rowKey}-${col}`).map(cellKey => (
              <div key={cellKey} className="flex flex-col gap-quark">
                <Skeleton className="h-mini w-[6rem] rounded-sm" />
                <Skeleton className="h-xs w-[11rem] rounded-sm" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Cabeçalho do contrato financeiro: régua de indicadores e card "Dados do
 * contrato".
 *
 * A régua é **um card único dividido por hairlines verticais**, não quatro cards
 * soltos — os quatro números são leituras do mesmo contrato, e separá-los em
 * caixas sugeriria que são entidades independentes.
 *
 * `status` e `type` chegam do backend como `number` sem texto correspondente.
 * São exibidos como o código recebido, sem tradução: um mapa adivinhado no
 * frontend exibiria o rótulo errado com confiança quando o backend introduzisse
 * um id novo, enquanto o código cru apenas fica pouco informativo e ainda pode
 * ser correlacionado com o sistema legado.
 */
export function ContractHeaderCard({ header, isLoading, summary }: ContractHeaderCardProps) {
  const { t } = useTranslation('individualPayment');
  const { label: paymentTypeLabel } = usePaymentTypeLabel(header?.paymentType);

  if (isLoading) {
    return (
      <>
        <StatRowSkeleton />
        <ContractCardSkeleton />
      </>
    );
  }

  const surcharge =
    header?.totalValuePaid != null &&
    header?.totalValueWithScholarShipDiscount != null &&
    header.totalValuePaid > header.totalValueWithScholarShipDiscount
      ? header.totalValuePaid - header.totalValueWithScholarShipDiscount
      : null;

  const hasDiscount =
    header?.totalValue != null &&
    header?.totalValueWithScholarShipDiscount != null &&
    header.totalValueWithScholarShipDiscount < header.totalValue;

  /**
   * Rótulo traduzido do enum, com o código bruto como recuo.
   *
   * O backend devolve o par id + texto (`type`/`typeText`, `status`/`statusText`)
   * já traduzido na cultura da request — é ele que deve aparecer. O "Código N" só
   * entra quando o texto vem vazio, porque um id ainda diz mais que um traço, e
   * dá ao operador o que informar num relato.
   */
  const label = (text: string | null | undefined, value: number | null | undefined) => {
    if (text !== null && text !== undefined && text.trim() !== '') return text;
    return value === null || value === undefined
      ? EMPTY_VALUE
      : t('parcels.contract.rawCode', { value });
  };

  return (
    <>
      <div className="flex flex-wrap items-center rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-mini py-xxs">
        <Stat
          label={t('parcels.stats.totalValue')}
          value={formatCurrency(header?.totalValue)}
          hint={
            summary && summary.total > 0 ? t('parcels.table.count', { count: summary.total }) : null
          }
        />
        <div className="h-md w-px bg-ctx-layout-border" />
        <Stat
          label={t('parcels.stats.withScholarship')}
          value={formatCurrency(header?.totalValueWithScholarShipDiscount)}
          hint={hasDiscount ? null : t('parcels.stats.noDiscount')}
        />
        <div className="h-md w-px bg-ctx-layout-border" />
        <Stat
          label={t('parcels.stats.totalPaid')}
          value={formatCurrency(header?.totalValuePaid)}
          hint={
            surcharge !== null
              ? t('parcels.stats.surcharge', { value: formatCurrency(surcharge) })
              : null
          }
        />
        <div className="h-md w-px bg-ctx-layout-border" />
        <Stat
          label={t('parcels.stats.numberParcels')}
          value={header?.numberParcels != null ? String(header.numberParcels) : EMPTY_VALUE}
          hint={
            summary
              ? t('parcels.stats.breakdown', { paid: summary.paid, open: summary.open })
              : null
          }
        />
      </div>

      <section className="flex flex-col rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
        <header className="flex items-center gap-nano border-b-hairline border-ctx-layout-border px-xxs py-micro">
          <DocumentTextIcon className="h-mini w-mini shrink-0 text-ctx-content-placeholder" />
          <h2 className="ts-heading-xs text-ctx-content-title">{t('parcels.contract.title')}</h2>
        </header>

        <div className="flex flex-col gap-xxxs px-xxs py-xxxs">
          <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
            <Field
              label={t('parcels.contract.student')}
              hint={header?.studentName ? header.email : null}
            >
              <span className="flex flex-wrap items-center gap-nano">
                <span className="break-words ts-paragraph-xs !font-medium text-ctx-content-title">
                  {header?.studentName ?? EMPTY_VALUE}
                </span>
                {header?.status != null && (
                  <Tag
                    label={label(header.statusText, header.status)}
                    size="sm"
                    status="default"
                    custom={{ text: 'text-nowrap' }}
                  />
                )}
              </span>
            </Field>
            <Field label={t('parcels.contract.course')} value={header?.courseName ?? EMPTY_VALUE} />
            <Field
              label={t('parcels.contract.type')}
              value={label(header?.typeText, header?.type)}
            />
          </div>

          <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
            <Field
              label={t('parcels.contract.cpfCnpj')}
              value={header?.cpfCnpj ?? EMPTY_VALUE}
              hint={header?.merchantName ? null : header?.companyName}
            />
            {/* O último cartão entra como linha de apoio daqui, e só em contrato
                de cartão: em boleto o backend pode ter um cartão guardado de uma
                forma de pagamento anterior, e exibi-lo diria que a cobrança vai
                para ele. */}
            <Field label={t('parcels.contract.paymentType')}>
              <span className="break-words ts-paragraph-xs text-ctx-content-title">
                {paymentTypeLabel}
              </span>
              {header?.lastCreditCard && isCardPaymentType(header.paymentType) ? (
                <LastCreditCardLine card={header.lastCreditCard} />
              ) : null}
            </Field>
            <Field
              label={t('parcels.contract.valueRegistration')}
              value={formatCurrency(header?.valueRegistration)}
            />
          </div>

          <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
            <Field
              label={t('parcels.contract.merchant')}
              value={header?.merchantName ?? EMPTY_VALUE}
            />
            <Field
              label={t('parcels.contract.projectParcelCode')}
              value={header?.projectParcelCode ?? EMPTY_VALUE}
            />
            <Field
              label={t('parcels.contract.projectParcelEnrollment')}
              value={header?.projectParcelEnrollment ?? EMPTY_VALUE}
            />
          </div>
        </div>
      </section>
    </>
  );
}
